/**
 * SuperAdminDashboard
 *
 * Backoffice screen — accessible ONLY to users with plan_tier === 'admin'.
 *
 * ─── Architecture (B2B Workspace model) ───────────────────────────────────────
 *
 * Modules are licensed PER COMPANY.  This dashboard has two panels:
 *
 *  1. COMPANIES panel  (primary)  ★ PHASE 76.2.B RE-WIRED ★
 *     Shows every company with toggle switches for module subscriptions.
 *     The module catalog is now loaded dynamically from `modules_v76`
 *     (no hardcoded list) and toggles INSERT/DELETE rows in
 *     `company_module_subscriptions_v76` (the new Phase 76 evolutionary
 *     RBAC dictionary). Writes are gated by the
 *     `phase76_cms_v76_insert/update/delete` policies (super-admin only).
 *
 *  2. USERS panel  (secondary / collapsible)
 *     Shows every user profile with their plan_tier.
 *     Super-admin can change plan_tier (free / premium / admin) here.
 *
 * ─── Backend dependencies (already deployed) ──────────────────────────────────
 *
 *   • supabase/migrations/20260512_phase76_evolutionary_rbac.sql
 *       Created modules_v76, company_module_subscriptions_v76, etc.
 *   • supabase/migrations/20260513_phase76_1_evolutionary_rls.sql
 *       phase76_modules_v76_*       — read by authenticated, write by super-admin
 *       phase76_cms_v76_*           — read by company members, write by super-admin
 *
 * ─── Evolutionary parity note ─────────────────────────────────────────────────
 *
 *   The legacy `companies.active_modules` column is INTENTIONALLY still updated
 *   alongside the new `company_module_subscriptions_v76` writes so that the
 *   legacy CompanyContext / ModuleGuard / RequirePermission paths keep working
 *   during the incremental frontend cutover (Phase 76.2.D). Once 76.2.D lands
 *   the routing guards on the new `useHasModule` hook, the legacy mirror-write
 *   can be removed in a focused follow-up commit.
 */

import { useEffect, useState, useCallback } from 'react';
import { useCompany } from '@/context/CompanyContext';
import { RefreshCw, ShieldCheck, Users, Building2, Save, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Search, X, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, Zap, Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { supabase, __env } from '@/lib/supabase';
import type { PlanTier, AppModule } from '@/types/database';
import type { SkillDefinition, SkillCategory, SkillColor } from './skills/types';
import { CATEGORY_LABELS } from './skills/types';

// ─── Types — dynamic module catalog from modules_v76 ─────────────────────────

interface ModuleCatalogEntry {
  key:              string;
  display_name_he:  string;
  display_name_en:  string;
  icon:             string | null;
  sort_order:       number;
}

/**
 * Fallback emoji/description map keyed by module key.
 * If a future module is seeded server-side without an `icon` column value, we
 * fall back to a neutral 🔌 glyph — no hardcoded business logic.
 */
const MODULE_EMOJI: Record<string, string> = {
  cashflow:  '📊',
  hr:        '👥',
  inventory: '📦',
};

const PLAN_OPTIONS: PlanTier[]              = ['free', 'premium', 'admin'];
const PLAN_LABELS: Record<PlanTier, string> = { free: 'Free', premium: 'Premium', admin: 'Admin' };
const PLAN_COLORS: Record<PlanTier, string> = {
  free:    'bg-gray-100 text-gray-700',
  premium: 'bg-amber-100 text-amber-700',
  admin:   'bg-purple-100 text-purple-700',
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompanyRow {
  id:                 string;
  legal_name:         string;
  country:            string;
  status:             string;
  member_count:       number;
  /** Set of module keys this company is subscribed to via company_module_subscriptions_v76. */
  subscribed_modules: Set<string>;
  /** Legacy mirror — kept in lock-step with the v76 set for evolutionary parity. */
  legacy_modules:     AppModule[];
  /** Local saving state per module key */
  saving:             Record<string, boolean>;
}

interface UserRow {
  id:         string;
  email:      string | null;
  full_name:  string | null;
  plan_tier:  PlanTier;
  planDraft:  PlanTier | null;
  planSaving: boolean;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

// ─── Component ────────────────────────────────────────────────────────────────

export function SuperAdminDashboard() {
  // ── Company context — needed to refresh the sidebar after module toggles ───
  const { refreshCompanies: refreshCtxCompanies } = useCompany();

  // ── State ──────────────────────────────────────────────────────────────────
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogEntry[]>([]);
  const [companies,    setCompanies]    = useState<CompanyRow[]>([]);
  const [users,        setUsers]        = useState<UserRow[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [toast,        setToast]        = useState<{ msg: string; ok: boolean } | null>(null);
  const [searchCo,     setSearchCo]     = useState('');
  const [searchUser,   setSearchUser]   = useState('');
  const [showUsers,    setShowUsers]    = useState(false);
  const [showSkills,   setShowSkills]   = useState(false);
  const [skills,       setSkills]       = useState<SkillDefinition[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Partial<SkillDefinition> | null>(null);
  const [skillSearch,  setSkillSearch]  = useState('');

  // ── Toast helper ────────────────────────────────────────────────────────────
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (!__env.hasUrl || !__env.hasKey) {
        throw new Error(
          `Supabase env vars missing!\nURL: ${__env.urlPreview}\nKey: ${__env.keyPreview} (len: ${__env.keyLength})`,
        );
      }

      // 1. Load the dynamic module catalog from modules_v76 (Phase 76 dictionary).
      //    This replaces the previous hardcoded MODULES constant — the panel now
      //    automatically reflects any new modules seeded server-side.
      const { data: modData, error: modErr } = await supabase
        .from('modules_v76')
        .select('key, display_name_he, display_name_en, icon, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (modErr) throw modErr;

      const catalog: ModuleCatalogEntry[] = (modData ?? []).map(m => ({
        key:             m.key as string,
        display_name_he: (m.display_name_he as string) ?? (m.key as string),
        display_name_en: (m.display_name_en as string) ?? (m.key as string),
        icon:            (m.icon as string | null) ?? null,
        sort_order:      (m.sort_order as number) ?? 0,
      }));
      setModuleCatalog(catalog);

      // 2. Load all companies — order by legal_name (text, always exists).
      //    `active_modules` is selected ONLY for the legacy mirror — the new
      //    source of truth is `company_module_subscriptions_v76` (loaded below).
      const { data: coData, error: coErr } = await supabase
        .from('companies')
        .select('id, legal_name, country, status, active_modules')
        .order('legal_name');  // text column — safe, no date columns touched

      if (coErr) throw coErr;

      // 3. Load member counts per company
      const { data: memData, error: memErr } = await supabase
        .from('company_members')
        .select('company_id');

      if (memErr) throw memErr;

      const memberCounts: Record<string, number> = {};
      (memData ?? []).forEach(m => {
        memberCounts[m.company_id] = (memberCounts[m.company_id] ?? 0) + 1;
      });

      // 4. Load all active subscriptions from the new v76 dictionary table.
      //    This is the authoritative source of truth for which modules a
      //    company is licensed to use under the Phase 76 RBAC model.
      const { data: subData, error: subErr } = await supabase
        .from('company_module_subscriptions_v76')
        .select('company_id, module_key');

      if (subErr) throw subErr;

      const subsByCompany: Record<string, Set<string>> = {};
      (subData ?? []).forEach(s => {
        const cid = s.company_id as string;
        const key = s.module_key as string;
        if (!subsByCompany[cid]) subsByCompany[cid] = new Set<string>();
        subsByCompany[cid].add(key);
      });

      const coRows: CompanyRow[] = (coData ?? []).map(c => ({
        id:                 c.id,
        legal_name:         c.legal_name,
        country:            c.country,
        status:             c.status,
        member_count:       memberCounts[c.id] ?? 0,
        subscribed_modules: subsByCompany[c.id] ?? new Set<string>(),
        legacy_modules:     (c.active_modules as AppModule[] | null) ?? [],
        saving:             {},
      }));

      setCompanies(coRows);

      // 3. Load all user profiles
      // ★ NO date columns selected, NO .order() by date.
      //   The profiles table column name (created_at vs updated_at) differs
      //   between DB instances and has caused repeated 42703 crashes.
      //   We don't need a date here — just email, name, plan_tier.
      const { data: profData, error: profErr } = await supabase
        .from('profiles')
        .select('id, email, full_name, plan_tier');
      //  ↑ no .order() — default DB order is fine for an admin list

      if (profErr) throw profErr;

      const userRows: UserRow[] = (profData ?? []).map(p => ({
        id:        p.id,
        email:     p.email,
        full_name: p.full_name,
        plan_tier: (p.plan_tier as PlanTier) ?? 'free',
        planDraft:  null,
        planSaving: false,
      }));

      setUsers(userRows);
    } catch (e) {
      const msg = e instanceof Error
        ? e.message
        : typeof e === 'object' && e !== null
          ? JSON.stringify(e)
          : String(e);
      console.error('[SuperAdmin] fetchAll:', e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toggle a module for a company ───────────────────────────────────────────
  //
  // PHASE 76.2.B: the authoritative write goes against
  // `company_module_subscriptions_v76` (INSERT to activate, DELETE to deactivate).
  // A best-effort mirror write to the legacy `companies.active_modules` column
  // keeps the legacy CompanyContext / ModuleGuard paths working during the
  // incremental cutover (Phase 76.2.D will retire this mirror).
  const toggleModule = useCallback(async (companyId: string, moduleKey: string) => {
    setCompanies(prev => prev.map(c => {
      if (c.id !== companyId) return c;
      return { ...c, saving: { ...c.saving, [moduleKey]: true } };
    }));

    const company = companies.find(c => c.id === companyId);
    if (!company) return;

    const hasIt = company.subscribed_modules.has(moduleKey);

    // 1. Authoritative write against company_module_subscriptions_v76 ───────────
    let writeErr: { message: string } | null = null;
    if (hasIt) {
      const { error: delErr } = await supabase
        .from('company_module_subscriptions_v76')
        .delete()
        .eq('company_id', companyId)
        .eq('module_key', moduleKey);
      writeErr = delErr;
    } else {
      const { error: insErr } = await supabase
        .from('company_module_subscriptions_v76')
        .insert({ company_id: companyId, module_key: moduleKey });
      writeErr = insErr;
    }

    if (writeErr) {
      showToast(`שגיאה בעדכון מודול: ${writeErr.message}`, false);
      setCompanies(prev => prev.map(c =>
        c.id === companyId ? { ...c, saving: { ...c.saving, [moduleKey]: false } } : c,
      ));
      return;
    }

    // 2. Best-effort legacy mirror write to companies.active_modules ───────────
    //    Only applies if the module key is a known AppModule literal — otherwise
    //    the legacy column simply omits it (acceptable during the cutover).
    const newLegacy: AppModule[] = hasIt
      ? company.legacy_modules.filter(m => m !== (moduleKey as AppModule))
      : Array.from(new Set<AppModule>([...company.legacy_modules, moduleKey as AppModule]));

    const { error: mirrorErr } = await supabase
      .from('companies')
      .update({ active_modules: newLegacy })
      .eq('id', companyId);

    // Mirror failure is non-fatal — the v76 write succeeded, so the new RBAC
    // path is correct. Log to console so it surfaces in dev, but don't toast.
    if (mirrorErr) {
      console.warn('[SuperAdmin] legacy active_modules mirror write failed:', mirrorErr);
    }

    // 3. Optimistic state update ────────────────────────────────────────────────
    setCompanies(prev => prev.map(c => {
      if (c.id !== companyId) return c;
      const nextSet = new Set(c.subscribed_modules);
      if (hasIt) nextSet.delete(moduleKey); else nextSet.add(moduleKey);
      return {
        ...c,
        subscribed_modules: nextSet,
        legacy_modules:     newLegacy,
        saving:             { ...c.saving, [moduleKey]: false },
      };
    }));

    const catalogEntry = moduleCatalog.find(m => m.key === moduleKey);
    const label = catalogEntry?.display_name_he ?? moduleKey;
    showToast(
      hasIt
        ? `${label} הושבת עבור ${company.legal_name}`
        : `${label} הופעל עבור ${company.legal_name}`,
    );
    // ★ Refresh the CompanyContext so the Sidebar immediately reflects the change.
    refreshCtxCompanies();
  }, [companies, moduleCatalog, showToast, refreshCtxCompanies]);

  const loadSkills = useCallback(async () => {
    setSkillsLoading(true);
    const { data } = await supabase
      .from('skill_definitions')
      .select('*')
      .order('sort_order');
    setSkills((data as SkillDefinition[]) ?? []);
    setSkillsLoading(false);
  }, []);

  useEffect(() => {
    if (showSkills && skills.length === 0) loadSkills();
  }, [showSkills, skills.length, loadSkills]);

  const togglePublish = useCallback(async (skill: SkillDefinition) => {
    const { error } = await supabase
      .from('skill_definitions')
      .update({ is_published: !skill.is_published })
      .eq('id', skill.id);
    if (!error) setSkills(prev => prev.map(s => s.id === skill.id ? { ...s, is_published: !s.is_published } : s));
  }, []);

  const deleteSkill = useCallback(async (id: string) => {
    if (!window.confirm('למחוק את הסקיל לצמיתות?')) return;
    await supabase.from('skill_definitions').delete().eq('id', id);
    setSkills(prev => prev.filter(s => s.id !== id));
  }, []);

  const saveSkill = useCallback(async (skill: Partial<SkillDefinition>) => {
    if (!skill.name_he || !skill.name_en || !skill.category) return;
    if (skill.id) {
      const { error } = await supabase.from('skill_definitions').update(skill).eq('id', skill.id);
      if (!error) {
        setSkills(prev => prev.map(s => s.id === skill.id ? { ...s, ...skill } as SkillDefinition : s));
        showToast('סקיל עודכן');
      } else showToast(`שגיאה: ${error.message}`, false);
    } else {
      const { data, error } = await supabase
        .from('skill_definitions')
        .insert({ ...skill, is_published: false, sort_order: skills.length * 10 + 10 })
        .select()
        .single();
      if (!error && data) {
        setSkills(prev => [...prev, data as SkillDefinition]);
        showToast('סקיל נוצר');
      } else showToast(`שגיאה: ${error?.message}`, false);
    }
    setEditingSkill(null);
  }, [skills.length, showToast]);

  // ── Save plan_tier ──────────────────────────────────────────────────────────
  const savePlanTier = useCallback(async (u: UserRow) => {
    if (!u.planDraft || u.planDraft === u.plan_tier) return;
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, planSaving: true } : x));

    const { error: err } = await supabase
      .from('profiles')
      .update({ plan_tier: u.planDraft })
      .eq('id', u.id);

    if (err) {
      showToast(`שגיאה בעדכון תוכנית: ${err.message}`, false);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, planSaving: false } : x));
    } else {
      setUsers(prev => prev.map(x =>
        x.id === u.id
          ? { ...x, plan_tier: u.planDraft!, planDraft: null, planSaving: false }
          : x,
      ));
      showToast(`תוכנית עודכנה ל-${PLAN_LABELS[u.planDraft!]} עבור ${u.email ?? u.id}`);
    }
  }, [showToast]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const qCo   = searchCo.toLowerCase();
  const qUser = searchUser.toLowerCase();

  const filteredCompanies = companies.filter(c =>
    !qCo ||
    c.legal_name.toLowerCase().includes(qCo) ||
    c.country.toLowerCase().includes(qCo),
  );

  const filteredUsers = users.filter(u =>
    !qUser ||
    (u.email     ?? '').toLowerCase().includes(qUser) ||
    (u.full_name ?? '').toLowerCase().includes(qUser),
  );

  const totalCompanies = companies.length;
  const totalUsers     = users.length;
  const premiumCount   = users.filter(u => u.plan_tier === 'premium').length;
  const adminCount     = users.filter(u => u.plan_tier === 'admin').length;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6" dir="rtl">

      {/* Toast */}
      {toast && (
        <div className={`
          fixed top-4 left-1/2 -translate-x-1/2 z-50
          flex items-center gap-2 px-5 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.ok
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
            : 'bg-red-50 border border-red-200 text-red-800'}
        `}>
          {toast.ok
            ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            : <AlertCircle  className="w-4 h-4 text-red-600" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-purple-100">
            <ShieldCheck className="w-6 h-6 text-purple-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">לוח בקרה — Super Admin</h1>
            <p className="text-sm text-gray-500">ניהול מודולים לפי חברה · ניהול תוכניות משתמשים</p>
          </div>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          רענן
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'חברות',       value: totalCompanies, icon: <Building2 className="w-5 h-5 text-teal-500" />,   bg: 'bg-teal-50'   },
          { label: 'משתמשים',     value: totalUsers,     icon: <Users     className="w-5 h-5 text-blue-500" />,   bg: 'bg-blue-50'   },
          { label: 'Premium',     value: premiumCount,   icon: <span className="text-lg">✨</span>,                bg: 'bg-amber-50'  },
          { label: 'Admin',       value: adminCount,     icon: <span className="text-lg">⚡</span>,                bg: 'bg-purple-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3 shadow-sm`}>
            {s.icon}
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Env diagnostics */}
      <details className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-medium text-gray-500 hover:text-gray-700 select-none">
          🔧 אבחון סביבה (Environment diagnostics)
        </summary>
        <div className="px-4 pb-3 pt-1 space-y-1 text-xs font-mono">
          <p className={__env.hasUrl  ? 'text-emerald-600' : 'text-red-600'}>
            VITE_SUPABASE_URL: {__env.urlPreview} {__env.hasUrl ? '✓' : '✗ MISSING'}
          </p>
          <p className={__env.hasKey ? 'text-emerald-600' : 'text-red-600'}>
            VITE_SUPABASE_ANON_KEY: {__env.keyPreview} (length: {__env.keyLength}) {__env.hasKey ? '✓' : '✗ MISSING'}
          </p>
        </div>
      </details>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-500" />
          <div>
            <p className="font-medium">שגיאה בטעינת הנתונים</p>
            <pre className="text-red-500 mt-0.5 whitespace-pre-wrap text-xs">{error}</pre>
            <p className="text-xs text-red-400 mt-2">
              ייתכן שחסרות מדיניות RLS ל-super-admin. ראה קובץ ה-migration.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-3">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>טוען נתונים…</span>
        </div>
      ) : (
        <>
          {/* ══════════════════════════════════════════════════════════════════
              PANEL 1 — COMPANIES + MODULE TOGGLES
          ══════════════════════════════════════════════════════════════════ */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                חברות ומודולים
              </h2>
              {/* Company search */}
              <div className="relative w-64">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchCo}
                  onChange={e => setSearchCo(e.target.value)}
                  placeholder="חיפוש חברה…"
                  className="w-full pr-9 pl-8 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                />
                {searchCo && (
                  <button onClick={() => setSearchCo('')} className="absolute left-3 top-1/2 -translate-y-1/2">
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Table header */}
              <div
                style={{ gridTemplateColumns: `1fr 80px repeat(${moduleCatalog.length}, 140px)` }}
                className="grid gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                <span>חברה</span>
                <span className="text-center">חברים</span>
                {moduleCatalog.map(m => (
                  <span key={m.key} className="text-center">
                    {m.icon ?? MODULE_EMOJI[m.key] ?? '🔌'} {m.display_name_he}
                  </span>
                ))}
              </div>

              {filteredCompanies.length === 0 && (
                <div className="py-12 text-center text-sm text-gray-400">לא נמצאו חברות</div>
              )}

              {filteredCompanies.map(company => (
                <div
                  key={company.id}
                  style={{ gridTemplateColumns: `1fr 80px repeat(${moduleCatalog.length}, 140px)` }}
                  className="grid gap-3 items-center px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60 transition-colors"
                >
                  {/* Company info */}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{company.legal_name}</p>
                    <p className="text-xs text-gray-400">
                      {company.country} ·{' '}
                      <span className={`font-medium ${
                        company.status === 'verified'   ? 'text-emerald-600' :
                        company.status === 'suspended'  ? 'text-red-600'     :
                        'text-amber-600'
                      }`}>
                        {company.status}
                      </span>
                    </p>
                  </div>

                  {/* Member count */}
                  <div className="flex justify-center">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Users className="w-3.5 h-3.5" />
                      {company.member_count}
                    </span>
                  </div>

                  {/* Module toggles — dynamic catalog from modules_v76 */}
                  {moduleCatalog.map(mod => {
                    const isActive = company.subscribed_modules.has(mod.key);
                    const isSaving = company.saving[mod.key] ?? false;
                    const label    = mod.display_name_he;
                    return (
                      <div key={mod.key} className="flex justify-center">
                        <button
                          onClick={() => toggleModule(company.id, mod.key)}
                          disabled={isSaving}
                          title={isActive ? `השבת ${label}` : `הפעל ${label}`}
                          className={`
                            flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                            transition-all duration-200 disabled:opacity-50
                            ${isActive
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'}
                          `}
                        >
                          {isSaving ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : isActive ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                          {isActive ? 'פעיל' : 'כבוי'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════════════════
              PANEL 2 — USERS / PLAN TIERS  (collapsible)
          ══════════════════════════════════════════════════════════════════ */}
          <section className="space-y-3">
            <button
              onClick={() => setShowUsers(v => !v)}
              className="flex items-center gap-2 text-lg font-semibold text-gray-800 hover:text-gray-600"
            >
              <Users className="w-5 h-5 text-blue-600" />
              משתמשים ותוכניות
              {showUsers
                ? <ChevronUp   className="w-4 h-4 text-gray-400" />
                : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showUsers && (
              <>
                {/* User search */}
                <div className="relative w-72">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchUser}
                    onChange={e => setSearchUser(e.target.value)}
                    placeholder="חיפוש לפי אימייל או שם…"
                    className="w-full pr-9 pl-8 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                  />
                  {searchUser && (
                    <button onClick={() => setSearchUser('')} className="absolute left-3 top-1/2 -translate-y-1/2">
                      <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_180px] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <span>משתמש</span>
                    <span className="text-center">תוכנית</span>
                  </div>

                  {filteredUsers.length === 0 && (
                    <div className="py-12 text-center text-sm text-gray-400">לא נמצאו משתמשים</div>
                  )}

                  {filteredUsers.map(u => {
                    const effectivePlan = u.planDraft ?? u.plan_tier;
                    const planChanged   = u.planDraft !== null && u.planDraft !== u.plan_tier;

                    return (
                      <div
                        key={u.id}
                        className="grid grid-cols-[1fr_180px] gap-4 items-center px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60 transition-colors"
                      >
                        {/* User info */}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {u.full_name || u.email || u.id}
                          </p>
                          {u.full_name && u.email && (
                            <p className="text-xs text-gray-400 truncate">{u.email}</p>
                          )}
                        </div>

                        {/* Plan tier */}
                        <div className="flex items-center gap-1.5 justify-center">
                          <span className={`
                            inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold
                            ${PLAN_COLORS[u.plan_tier]}
                          `}>
                            {PLAN_LABELS[u.plan_tier]}
                          </span>
                          <select
                            value={effectivePlan}
                            onChange={e =>
                              setUsers(prev => prev.map(x =>
                                x.id === u.id ? { ...x, planDraft: e.target.value as PlanTier } : x,
                              ))
                            }
                            disabled={u.planSaving}
                            className="text-xs border border-gray-200 rounded-lg px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 cursor-pointer disabled:opacity-50"
                          >
                            {PLAN_OPTIONS.map(p => (
                              <option key={p} value={p}>{PLAN_LABELS[p]}</option>
                            ))}
                          </select>
                          {planChanged && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => savePlanTier(u)}
                                disabled={u.planSaving}
                                title="שמור"
                                className="p-1 rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-50"
                              >
                                <Save className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() =>
                                  setUsers(prev => prev.map(x =>
                                    x.id === u.id ? { ...x, planDraft: null } : x,
                                  ))
                                }
                                disabled={u.planSaving}
                                title="בטל"
                                className="p-1 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>


                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
          {/* ══════════════════════════════════════════════════════════════════
              PANEL 3 — SKILLS CATALOG  (collapsible)
          ══════════════════════════════════════════════════════════════════ */}
          <section className="space-y-3">
            <button
              onClick={() => setShowSkills(v => !v)}
              className="flex items-center gap-2 text-lg font-semibold text-gray-800 hover:text-gray-600"
            >
              <Zap className="w-5 h-5 text-blue-600" />
              ניהול קטלוג סקילים
              {showSkills
                ? <ChevronUp   className="w-4 h-4 text-gray-400" />
                : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showSkills && (
              <>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={skillSearch} onChange={e => setSkillSearch(e.target.value)}
                      placeholder="חיפוש סקיל..."
                      className="w-full pr-9 pl-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
                  </div>
                  <button onClick={() => setEditingSkill({ category: 'hr', color: 'blue', icon: 'Zap', estimated_minutes_saved: 30, estimated_cost_saved: 50, input_schema: [] })}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />
                    סקיל חדש
                  </button>
                  <button onClick={loadSkills} disabled={skillsLoading} className="p-2 text-gray-400 hover:text-gray-600">
                    <RefreshCw className={`w-4 h-4 ${skillsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="grid grid-cols-[2fr_1fr_80px_80px_80px_100px] gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <span>שם</span><span>קטגוריה</span><span className="text-center">זמן</span><span className="text-center">עלות ₪</span><span className="text-center">סטטוס</span><span className="text-center">פעולות</span>
                  </div>

                  {skillsLoading && (
                    <div className="py-8 flex items-center justify-center text-gray-400 gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />טוען...
                    </div>
                  )}

                  {!skillsLoading && skills.filter(s => !skillSearch || s.name_he.includes(skillSearch) || s.name_en.toLowerCase().includes(skillSearch.toLowerCase())).map(skill => (
                    <div key={skill.id}
                      className="grid grid-cols-[2fr_1fr_80px_80px_80px_100px] gap-3 items-center px-5 py-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{skill.name_he}</p>
                        <p className="text-xs text-gray-400 truncate">{skill.name_en}</p>
                      </div>
                      <span className="text-xs text-gray-600">{CATEGORY_LABELS[skill.category as SkillCategory] ?? skill.category}</span>
                      <span className="text-xs text-center text-gray-600">{skill.estimated_minutes_saved} דק'</span>
                      <span className="text-xs text-center text-emerald-700 font-medium">₪{skill.estimated_cost_saved}</span>
                      <div className="flex justify-center">
                        <button onClick={() => togglePublish(skill)}
                          title={skill.is_published ? 'הסתר מהחנות' : 'פרסם לחנות'}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                            ${skill.is_published ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                          {skill.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          {skill.is_published ? 'פעיל' : 'טיוטה'}
                        </button>
                      </div>
                      <div className="flex justify-center gap-1">
                        <button onClick={() => setEditingSkill({ ...skill })} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" title="עריכה">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteSkill(skill.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors" title="מחיקה">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {!skillsLoading && skills.length === 0 && (
                    <div className="py-8 text-center text-sm text-gray-400">אין סקילים בקטלוג</div>
                  )}
                </div>
              </>
            )}
          </section>
        </>
      )}

      {/* ── Skill edit modal ───────────────────────────────────────────────── */}
      {editingSkill !== null && (
        <SkillEditModal
          skill={editingSkill}
          onSave={saveSkill}
          onClose={() => setEditingSkill(null)}
        />
      )}

      <p className="text-xs text-gray-400 text-center pb-4">
        פאנל זה גלוי אך ורק למשתמשים עם plan_tier = 'admin' ·
        שינויים מיושמים ישירות ב-Supabase
      </p>
    </div>
  );
}

// ─── Skill Edit Modal ────────────────────────────────────────────────────────

const CATEGORY_OPTIONS: SkillCategory[] = ['hr', 'accounting', 'inventory', 'sales', 'integrations', 'custom'];
const COLOR_OPTIONS: SkillColor[] = ['blue', 'emerald', 'amber', 'rose', 'slate', 'violet'];
const ICON_OPTIONS = ['Zap', 'Clock', 'Building2', 'Package', 'TrendingUp', 'UserPlus', 'BarChart3', 'Users', 'Star', 'PlugZap'];

function SkillEditModal({ skill, onSave, onClose }: {
  skill: Partial<SkillDefinition>;
  onSave: (s: Partial<SkillDefinition>) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<Partial<SkillDefinition>>(skill);
  const [saving, setSaving] = useState(false);

  const set = (key: keyof SkillDefinition, value: unknown) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{form.id ? 'עריכת סקיל' : 'סקיל חדש'}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <Field label="שם בעברית *">
            <input value={form.name_he ?? ''} onChange={e => set('name_he', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>
          <Field label="שם באנגלית *">
            <input value={form.name_en ?? ''} onChange={e => set('name_en', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>

          <div className="col-span-2">
            <Field label="תיאור בעברית">
              <textarea value={form.description_he ?? ''} onChange={e => set('description_he', e.target.value)} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="תיאור באנגלית">
              <textarea value={form.description_en ?? ''} onChange={e => set('description_en', e.target.value)} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </Field>
          </div>

          <Field label="קטגוריה *">
            <select value={form.category ?? 'hr'} onChange={e => set('category', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
          </Field>
          <Field label="צבע">
            <select value={form.color ?? 'blue'} onChange={e => set('color', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="אייקון">
            <select value={form.icon ?? 'Zap'} onChange={e => set('icon', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="חיסכון בזמן (דקות)">
            <input type="number" value={form.estimated_minutes_saved ?? 30} onChange={e => set('estimated_minutes_saved', Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>
          <Field label="חיסכון בכסף (₪ לריצה)">
            <input type="number" value={form.estimated_cost_saved ?? 0} onChange={e => set('estimated_cost_saved', Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>
          <Field label="סדר תצוגה">
            <input type="number" value={form.sort_order ?? 0} onChange={e => set('sort_order', Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>

          <div className="col-span-2">
            <Field label="הוראות ל-AI (Custom Prompt)">
              <textarea
                value={form.custom_prompt ?? ''}
                onChange={e => set('custom_prompt', e.target.value || null)}
                rows={6}
                dir="rtl"
                placeholder="כתוב כאן בשפה חופשית מה ה-AI צריך לעשות כשמפעילים את הסקיל הזה. לדוגמה: נתח את הקובץ המצורף, חשב לכל עובד סה״כ שעות רגילות ושעות נוספות, והצג טבלה ממוינת..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              />
            </Field>
            <p className="mt-1 text-[11px] text-gray-400">הטקסט ישלח ישירות ל-Claude כשמשתמש מפעיל את הסקיל. ככל שתהיה יותר ספציפי, התוצאה תהיה יותר מדויקת. אם לא מולא — הסקיל יפעל עם prompt אוטומטי.</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">ביטול</button>
          <button onClick={handleSave} disabled={saving || !form.name_he || !form.name_en}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
            {saving && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
            שמור
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
